import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------ */
/*  Icons — same lightweight inline SVG set used in Dynamicgraph       */
/* ------------------------------------------------------------------ */
const Icon = ({ children, size = 16, className = "", ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...rest}
  >
    {children}
  </svg>
);

const makeIcon = (children) => (props) => <Icon {...props}>{children}</Icon>;

const IconSearch = makeIcon(
  <>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </>,
);
const IconFilter = makeIcon(
  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />,
);
const IconChevronDown = makeIcon(<polyline points="6 9 12 15 18 9" />);
const IconFileText = makeIcon(
  <>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </>,
);
const IconCheckCircle = makeIcon(
  <>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </>,
);
const IconLoader = makeIcon(
  <>
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </>,
);
const IconClock = makeIcon(
  <>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </>,
);
const IconInbox = makeIcon(
  <>
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </>,
);

/* ------------------------------------------------------------------ */
/*  Status config (keeps the original status codes 0 / 1 / 2 / 3)      */
/* ------------------------------------------------------------------ */
const STATUS = {
  3: {
    label: "Report generated",
    badge: "bg-[#EAF7F1] text-[#1B8A5A]",
    dot: "bg-[#1B8A5A]",
  },
  2: {
    label: "Processing…",
    badge: "bg-[#FEF5E0] text-[#B7791F]",
    dot: "bg-[#FEB13F]",
  },
  1: {
    label: "Yet to process",
    badge: "bg-slate-100 text-slate-600",
    dot: "bg-slate-400",
  },
  0: {
    label: "Uploading file",
    badge: "bg-[#154A79]/[0.08] text-[#154A79]",
    dot: "bg-[#154A79]",
  },
};
const STATUS_UNKNOWN = {
  label: "File not found",
  badge: "bg-[#FDEDED] text-[#D64545]",
  dot: "bg-[#D64545]",
};

const StatusBadge = ({ status }) => {
  const s = STATUS[String(status)] || STATUS_UNKNOWN;
  return (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${s.badge}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, tone = "default" }) => {
  const tones = {
    default: "text-[#154A79] bg-[#154A79]/[0.06]",
    amber: "text-[#B7791F] bg-[#FEF5E0]",
    good: "text-[#1B8A5A] bg-[#EAF7F1]",
    muted: "text-slate-500 bg-slate-100",
  };
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-slate-400">{label}</p>
        <p className="text-lg font-bold text-slate-800">{value}</p>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  DataTable theme                                                     */
/* ------------------------------------------------------------------ */
const customStyles = {
  table: { style: { backgroundColor: "transparent" } },
  headRow: {
    style: {
      backgroundColor: "#F8FAFC", // slate-50
      borderBottom: "1px solid #E2E8F0", // slate-200
      minHeight: "48px",
    },
  },
  headCells: {
    style: {
      color: "#64748B", // slate-500
      fontSize: "12px",
      fontWeight: 600,
      letterSpacing: "0.02em",
    },
  },
  rows: {
    style: {
      minHeight: "68px",
      color: "#334155", // slate-700
      fontSize: "14px",
      borderBottom: "1px solid #F1F5F9", // slate-100
    },
    highlightOnHoverStyle: {
      backgroundColor: "#F8FAFC",
      color: "#154A79",
      borderBottomColor: "#F1F5F9",
      outline: "none",
    },
  },
  pagination: {
    style: {
      color: "#64748B",
      fontSize: "13px",
      borderTop: "1px solid #E2E8F0",
      backgroundColor: "#FFFFFF",
      minHeight: "56px",
    },
    pageButtonsStyle: {
      borderRadius: "9999px",
      height: "36px",
      width: "36px",
      padding: "6px",
      margin: "0 2px",
      cursor: "pointer",
      color: "#154A79",
      fill: "#154A79",
      backgroundColor: "transparent",
      "&:hover:not(:disabled)": { backgroundColor: "#F1F5F9" },
      "&:focus": { outline: "none", backgroundColor: "#FEF5E0" },
      "&:disabled": {
        cursor: "not-allowed",
        color: "#CBD5E1",
        fill: "#CBD5E1",
      },
    },
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */
const Table = ({ files, setCurrentrequest }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [filetoshow, setFiletoshow] = useState(" ");

  useEffect(() => {
    setData(
      files.map((item, index) => ({
        sno: index + 1,
        id: item.id,
        name: item.name,
        publishername: "Publishername",
        journalname: "Journalname",
        status: item.status,
        time: item.time,
      })),
    );
  }, [files]);

  useEffect(() => {
    const [c_id, exits] = (filetoshow || " ").split(" = ");
    setCurrentrequest({ c_id, exits });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filetoshow]);

  const columns = [
    {
      name: "S.No",
      selector: (row) => row.sno,
      sortable: true,
      width: "80px",
    },
    // {
    //   name: "File ID",
    //   selector: (row) => row.id,
    //   sortable: true,
    //   width: "8%",
    // },
    {
      name: "File Name",
      selector: (row) => row.name,
      sortable: true,
      width: "15%",
      wrap: true,
      cell: (row) => (
        <span className="flex items-center gap-2.5 py-2 font-medium text-slate-700">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#154A79]/[0.06] text-[#154A79]">
            <IconFileText size={15} />
          </span>
          <span className="break-all">{row.name}</span>
        </span>
      ),
    },
    {
      name: "Publisher Name",
      selector: (row) => row.publishername,
      sortable: true,
    },
    {
      name: "Journal Name",
      selector: (row) => row.journalname,
      sortable: true,
    },
    {
      name: "Upload Time",
      selector: (row) => row.time,
      sortable: true,
    },
    {
      name: "Report",
      cell: (row) =>
        String(row.status) === "3" ? (
          <Link
            to={`/report/${row.id}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 px-3.5 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-[#FEB13F] hover:text-[#154A79]"
          >
            <IconFileText size={14} />
            View report
          </Link>
        ) : (
          <span className="text-sm text-slate-400">Report unavailable</span>
        ),
    },
    {
      name: "File Status",
      minWidth: "170px",
      cell: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const filteredData = data.filter((item) =>
    String(item.name ?? "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const stats = useMemo(() => {
    const count = (s) => data.filter((d) => String(d.status) === s).length;
    return {
      total: data.length,
      ready: count("3"),
      processing: count("2"),
      queued: count("1"),
    };
  }, [data]);

  const handleSelect = (e) => {
    setFiletoshow(e.target.value);
  };

  const NoRows = () => (
    <div className="flex w-full flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-300">
        {searchTerm ? <IconSearch size={20} /> : <IconInbox size={20} />}
      </div>
      <p className="text-sm font-semibold text-slate-500">
        {searchTerm
          ? `No files match “${searchTerm}”`
          : "No files uploaded yet"}
      </p>
      {searchTerm && (
        <button
          onClick={() => setSearchTerm("")}
          className="text-sm font-medium text-[#154A79] underline underline-offset-2"
        >
          Clear search
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-full w-full bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-[80px] z-20 flex w-full flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur md:px-8">
        <div className="min-w-0">
          <h2 className="truncate text-xl font-bold text-[#154A79] md:text-2xl">
            File History
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 md:text-sm">
            Track your uploads and open generated reports
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="relative">
            <IconFilter
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <select
              value={filetoshow}
              onChange={handleSelect}
              aria-label="Filter files"
              className="w-60 cursor-pointer appearance-none rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-9 text-sm text-slate-600 outline-none transition-colors focus:border-[#FEB13F] focus:bg-white md:w-72"
            >
              <option value={" "}>Show all</option>
              <optgroup label="Author Department Relevance to Journal">
                <option value={"26 = True"}>
                  Author Department Relevance to Journal True
                </option>
                <option value={"26 = False"}>
                  Author Department Relevance to Journal False
                </option>
              </optgroup>
              <optgroup label="Reference Relevance to Journal">
                <option value={"24 = True"}>
                  Reference Relevance to Journal True
                </option>
                <option value={"24 = False"}>
                  Reference Relevance to Journal False
                </option>
              </optgroup>
            </select>
            <IconChevronDown
              size={15}
              className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
          </div>

          <div className="relative">
            <IconSearch
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search by file name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-56 rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-600 outline-none transition-colors focus:border-[#FEB13F] focus:bg-white md:w-64"
            />
          </div>
        </div>
      </header>

      <main className="px-6 py-8 md:px-10">
        {/* Summary stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            icon={<IconFileText size={18} />}
            label="Total files"
            value={stats.total}
          />
          <StatCard
            icon={<IconCheckCircle size={18} />}
            label="Reports ready"
            value={stats.ready}
            tone="good"
          />
          <StatCard
            icon={<IconLoader size={18} />}
            label="Processing"
            value={stats.processing}
            tone="amber"
          />
          <StatCard
            icon={<IconClock size={18} />}
            label="Yet to process"
            value={stats.queued}
            tone="muted"
          />
        </div>

        {/* Table card */}
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <span className="h-7 w-1.5 shrink-0 rounded-full bg-[#FEB13F]" />
            <h3 className="text-xl font-semibold text-[#154A79]">All files</h3>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
              {filteredData.length}{" "}
              {filteredData.length === 1 ? "file" : "files"}
            </span>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              customStyles={customStyles}
              noDataComponent={<NoRows />}
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default Table;
